// Sliders (Embla) and the lightbox (PhotoSwipe 5) for the product gallery, the testimonial slider, the page-section
// images and the image grid. Replaced Swiper 6, PhotoSwipe 4 and featherlight in 2026
// (modernisation/design/frontend.md); markup in views/partials/product-gallery.php and testis.php.
import EmblaCarousel from 'embla-carousel';

// PhotoSwipe is lightbox.js (its versioned URL is on this script's tag, data-lightbox), loaded on first use: when a
// pointer comes over a lightbox link (so a click rarely waits for it), or at the click. If it can't be loaded, the
// click opens the photo itself, as a link.
const lightboxScript = document.currentScript?.dataset.lightbox
    || (document.currentScript ? new URL('lightbox.js', document.currentScript.src).href : null);
let photoSwipe = null;

function loadPhotoSwipe() {
    photoSwipe ??= new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = lightboxScript;
        s.onload = () => resolve(window.JFJPhotoSwipe);
        s.onerror = () => {
            photoSwipe = null;
            s.remove();
            reject(new Error('lightbox.js did not load'));
        };
        document.head.append(s);
    });
    return photoSwipe;
}

// Opens the links of one group in the lightbox at `index`. A link gives its image's size in data-pswp-width/height;
// without them (page-section images) the image is loaded first to measure it. `onClose` gets the index shown last.
async function openLightbox(links, index, onClose) {
    let PhotoSwipe;
    try {
        PhotoSwipe = await loadPhotoSwipe();
    } catch {
        window.location.href = links[index].href;
        return;
    }
    const items = await Promise.all(links.map(async (a) => {
        let width = +a.dataset.pswpWidth;
        let height = +a.dataset.pswpHeight;
        if (!width || !height) {
            const img = new Image();
            img.src = a.href;
            await img.decode().catch(() => {});
            width = img.naturalWidth || 1600;
            height = img.naturalHeight || 1200;
            a.dataset.pswpWidth = width;
            a.dataset.pswpHeight = height;
        }
        // The clicked thumbnail shows while the full image loads, stretched to the full image's shape, so only use it
        // when it has that shape: the product and grid thumbnails are square crops of wider photos.
        const thumb = a.querySelector('img');
        const sameShape = thumb && thumb.naturalWidth
            && Math.abs(thumb.naturalWidth / thumb.naturalHeight - width / height) < 0.02;
        return { src: a.href, width, height, msrc: sameShape ? thumb.currentSrc : undefined, alt: thumb ? thumb.alt : '', element: a };
    }));
    const pswp = new PhotoSwipe({ dataSource: items, index, bgOpacity: 0.9, showHideAnimationType: 'fade' });
    if (onClose) {
        pswp.on('close', () => onClose(pswp.currIndex));
    }
    pswp.init();
}

// Every link with data-lightbox opens in the lightbox, with the other links of the same group as its neighbours.
// Product galleries handle their own links (below), so the slider follows the lightbox.
function initLightboxLinks() {
    document.addEventListener('pointerover', (e) => {
        if (e.target.closest('a[data-lightbox]')) {
            loadPhotoSwipe().catch(() => {});
        }
    }, { passive: true });
    document.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-lightbox]');
        if (!a || a.closest('[data-product-gallery]')) {
            return;
        }
        e.preventDefault();
        // Only the links on show (an image grid's filter hides some).
        const group = [...document.querySelectorAll(`a[data-lightbox="${a.dataset.lightbox}"]`)].filter((x) => x.offsetParent);
        openLightbox(group, group.indexOf(a));
    });
}

function initProductGallery(root) {
    const links = [...root.querySelectorAll('a[data-lightbox]')];
    const main = EmblaCarousel(root.querySelector('.product-slider__viewport'), { loop: links.length > 1 });
    const thumbsViewport = root.querySelector('.product-thumbs__viewport');
    const thumbs = thumbsViewport ? EmblaCarousel(thumbsViewport, { dragFree: true, containScroll: 'keepSnaps' }) : null;
    const buttons = [...root.querySelectorAll('.product-thumbs__thumb')];

    const select = () => {
        const i = main.selectedScrollSnap();
        buttons.forEach((b, j) => b.classList.toggle('is-selected', i === j));
        if (thumbs) {
            thumbs.scrollTo(i);
        }
    };
    main.on('select', select);
    select();
    buttons.forEach((b, i) => b.addEventListener('click', () => main.scrollTo(i)));
    root.querySelector('.product-slider__arrow--prev')?.addEventListener('click', () => main.scrollPrev());
    root.querySelector('.product-slider__arrow--next')?.addEventListener('click', () => main.scrollNext());

    // A click (not a drag; Embla swallows those) opens the lightbox; closing it leaves the slider on the last image seen.
    links.forEach((a, i) => a.addEventListener('click', (e) => {
        e.preventDefault();
        openLightbox(links, i, (last) => main.scrollTo(last, true));
    }));

    // Arrow keys move the slider while it's on screen, as before; not while typing or with the lightbox open.
    document.addEventListener('keydown', (e) => {
        if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || document.querySelector('.pswp')
            || e.target.closest('input, textarea, select, [contenteditable]')) {
            return;
        }
        const r = root.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight) {
            e.key === 'ArrowLeft' ? main.scrollPrev() : main.scrollNext();
        }
    });
}

function initTestimonials(root) {
    const slider = EmblaCarousel(root.querySelector('.testimonials__viewport'));
    const dotsEl = root.querySelector('.testimonials__dots');
    const dots = slider.scrollSnapList().map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'testimonials__dot';
        b.setAttribute('aria-label', `Testimonial ${i + 1}`);
        b.addEventListener('click', () => slider.scrollTo(i));
        dotsEl.append(b);
        return b;
    });
    const select = () => dots.forEach((d, i) => d.classList.toggle('is-selected', i === slider.selectedScrollSnap()));
    slider.on('select', select);
    slider.on('reInit', select);
    select();
}

// Image grid (inc/gallery.php): the tag dropdown and the search box hide the photos that don't match.
function initImageGrid(root) {
    const tag = root.querySelector('.image-grid__tag');
    const search = root.querySelector('.image-grid__search');
    if (!tag || !search) {
        return;
    }
    const filter = () => {
        const t = tag.value;
        const q = search.value.trim().toLowerCase();
        root.querySelectorAll('.image-grid__item').forEach((item) => {
            const show = (!t || item.dataset.tags.split(',').includes(t)) && (!q || item.dataset.name.includes(q));
            item.hidden = !show;
        });
    };
    tag.addEventListener('change', filter);
    search.addEventListener('input', filter);
}

export function initGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
    document.querySelectorAll('[data-testimonials]').forEach(initTestimonials);
    document.querySelectorAll('[data-image-grid]').forEach(initImageGrid);
    initLightboxLinks();
}
